import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Mail, Lock, User, Phone, Key, CheckCircle, Store, MapPin, Globe, Calendar, Hash } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { register, validateInviteCode, getCategories, upgradeToCoupleRole } from '../services/api';
import { isNative, openGoogleSignIn } from '../utils/native';

// Default landing route for each role/dashboard
const ROLE_HOME = { couple: '/dashboard', helper: '/checklist', vendor_employee: '/my-projects', vendor: '/vendor-portal', admin: '/admin' };

function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const { user, loading: authLoading, loginUser } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '', phone: '', role: 'couple',
    inviteCode: '', businessName: '', categoryId: '', vendorCity: '',
    vendorWebsite: '', vendorSocialMedia: '', yearsInBusiness: '', vatPanNumber: '',
    coupleType: 'couple'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [emailSentOk, setEmailSentOk] = useState(true);
  const [codeValidation, setCodeValidation] = useState(null); // { valid, wedding, role }
  const [validatingCode, setValidatingCode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isHelperConflict, setIsHelperConflict] = useState(false);
  const [upgradePassword, setUpgradePassword] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  // The helper account to upgrade has no password (created with Google)
  const [upgradeNoPassword, setUpgradeNoPassword] = useState(false);

  // null until the auth check settles, then true if a session already existed
  const arrivedSignedIn = useRef(null);

  useEffect(() => {
    getCategories().then(res => setCategories(res.data)).catch(() => {});
  }, []);

  // Google sign-up popup result (same handshake as /login). Without a popup
  // the server lands on /login?token=, which handles it there.
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'social-auth-error' && event.data.error) {
        setError(event.data.error);
      } else if (event.data?.type === 'social-auth' && event.data.token && event.data.user) {
        loginUser(event.data.user, event.data.token);
        navigate(redirect || ROLE_HOME[event.data.user.role] || '/dashboard');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already signed in on arrival — the signup form would only create a second
  // account. Keyed on the session that existed at load so registering here (or
  // upgrading a helper account) still follows its own redirect.
  useEffect(() => {
    if (authLoading) return;
    if (arrivedSignedIn.current === null) arrivedSignedIn.current = !!user;
    if (arrivedSignedIn.current && user) {
      navigate(redirect || ROLE_HOME[user.role] || '/dashboard', { replace: true });
    }
  }, [user, authLoading, redirect, navigate]);

  const handleValidateCode = async () => {
    if (!form.inviteCode.trim()) return;
    setValidatingCode(true);
    setCodeValidation(null);
    try {
      const res = await validateInviteCode(form.inviteCode);
      setCodeValidation({ valid: true, wedding: res.data.wedding, role: res.data.role });
      setError('');
    } catch (err) {
      setCodeValidation({ valid: false });
      setError(err.response?.data?.error || 'Invalid invite code');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.role === 'helper' && !form.inviteCode.trim()) {
      setError('Please enter the invite code provided by the couple');
      return;
    }
    if (form.role === 'vendor' && !form.businessName.trim()) {
      setError('Business name is required for vendor registration');
      return;
    }
    setLoading(true);
    try {
      const res = await register(form);
      if (res.data.verificationRequired) {
        setEmailSentOk(res.data.emailSent !== false);
        setVerificationSent(true);
      } else {
        loginUser(res.data.user, res.data.token);
        navigate(redirect || '/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.isHelperAccount) {
        setIsHelperConflict(true);
        setError('');
      } else {
        setError(err.response?.data?.error || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToCoupleRole = async (e) => {
    e.preventDefault();
    setError('');
    setUpgradeNoPassword(false);
    setUpgradeLoading(true);
    try {
      const res = await upgradeToCoupleRole({ email: form.email, password: upgradePassword });
      loginUser(res.data.user, res.data.token);
      navigate(redirect || '/dashboard');
    } catch (err) {
      const msg = err.response?.data?.error || '';
      // "uses social login": there is no password to enter here; /login has
      // the Google button (web) and "Forgot password" (everywhere)
      if (err.response?.data?.code === 'GOOGLE_ACCOUNT' || /social login/i.test(msg)) setUpgradeNoPassword(true);
      setError(msg || 'Could not upgrade account. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {isHelperConflict && !verificationSent ? (
            <div style={{ padding: '8px 0' }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Heart size={40} color="#e11d48" style={{ marginBottom: '12px' }} />
                <h2 style={{ marginBottom: '8px' }}>Already a Helper?</h2>
                <p style={{ color: '#6b7280', lineHeight: 1.7, fontSize: '14px' }}>
                  The email <strong style={{ color: '#1f2937' }}>{form.email}</strong> is already registered as a helper account.
                  Enter your current password to also set up a couple account with the same email.
                </p>
              </div>
              {error && <div className="auth-error">{error}</div>}
              {upgradeNoPassword && (
                <p style={{ textAlign: 'center', fontSize: '14px', margin: '0 0 16px' }}>
                  <Link to="/login">{t('register.goToLogin')}</Link>
                </p>
              )}
              <form onSubmit={handleUpgradeToCoupleRole} className="auth-form">
                <div className="form-group">
                  <label><Lock size={16} /> Your Current Password</label>
                  <input
                    type="password"
                    placeholder="Enter your helper account password"
                    value={upgradePassword}
                    onChange={(e) => setUpgradePassword(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={upgradeLoading}>
                  {upgradeLoading ? 'Setting up...' : 'Continue as Couple Account'}
                </button>
              </form>
              <div className="auth-footer" style={{ marginTop: '16px' }}>
                <p>
                  <button
                    type="button"
                    onClick={() => { setIsHelperConflict(false); setUpgradePassword(''); setError(''); }}
                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px' }}
                  >
                    &larr; Back to registration
                  </button>
                </p>
              </div>
            </div>
          ) : verificationSent ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CheckCircle size={56} color="#22C55E" style={{ marginBottom: '16px' }} />
              <h1 style={{ marginBottom: '12px' }}>{t('register.checkEmail')}</h1>
              <p style={{ color: '#666', lineHeight: 1.7, marginBottom: '8px' }}>
                {t('register.verificationSent')}
              </p>
              <p style={{ fontWeight: 700, color: '#1f2937', fontSize: '16px', marginBottom: '20px' }}>
                {form.email}
              </p>
              {!emailSentOk && (
                <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'left' }}>
                  <p style={{ color: '#92400E', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
                    <strong>⚠ Email delivery issue:</strong> We had trouble sending the verification email. You can request a new one from the login page using "Resend Verification Email."
                  </p>
                </div>
              )}
              <p style={{ color: '#666', fontSize: '14px', lineHeight: 1.7, marginBottom: '24px' }}>
                {t('register.clickLink')}
              </p>
              <Link to="/login" className="btn btn-primary btn-block">
                {t('register.goToLogin')}
              </Link>
              <p style={{ color: '#999', fontSize: '13px', marginTop: '16px' }}>
                {t('register.didntReceive')}
              </p>
            </div>
          ) : (
          <>
          <div className="auth-header">
            <Heart className="auth-icon" size={32} />
            <h1>{t('register.startPlanning')}</h1>
            <p>{t('register.createAccount')}</p>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-row">
              <div className="form-group">
                <label><User size={16} /> {t('register.firstName')}</label>
                <input type="text" placeholder={t('register.firstName')} value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
              </div>
              <div className="form-group">
                <label><User size={16} /> {t('register.lastName')}</label>
                <input type="text" placeholder={t('register.lastName')} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label><Mail size={16} /> {t('register.email')}</label>
              <input type="email" placeholder="your@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="form-group">
              <label><Phone size={16} /> {t('register.phone')}</label>
              <input type="tel" placeholder="+977-98XXXXXXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('register.iAmA')}</label>
              <select value={form.role} onChange={(e) => { setForm({ ...form, role: e.target.value, inviteCode: '', businessName: '', categoryId: '', vendorCity: '', vendorWebsite: '', vendorSocialMedia: '', yearsInBusiness: '', vatPanNumber: '', coupleType: e.target.value === 'couple' ? 'couple' : '' }); setCodeValidation(null); setError(''); }}>
                <option value="couple">{t('register.coupleGettingMarried')}</option>
                <option value="vendor">{t('register.weddingVendor')}</option>
                <option value="helper">{t('register.weddingHelper')}</option>
              </select>
            </div>

            {form.role === 'couple' && (
              <div className="form-group">
                <label>{t('register.moreSpecifically')}</label>
                <select value={form.coupleType} onChange={(e) => setForm({ ...form, coupleType: e.target.value })}>
                  <option value="couple">{t('register.coupleGettingMarried')}</option>
                  <option value="individual">{t('register.individualPlanning')}</option>
                  <option value="parent">{t('register.parentOfBrideGroom')}</option>
                  <option value="friend">{t('register.friendHelping')}</option>
                </select>
              </div>
            )}

            {form.role === 'vendor' && (
              <>
                <div className="form-group">
                  <label><Store size={16} /> Business Name *</label>
                  <input type="text" placeholder="Your business name" value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                      <option value="">Select category...</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label><MapPin size={16} /> City</label>
                    <input type="text" placeholder="Kathmandu" value={form.vendorCity} onChange={(e) => setForm({ ...form, vendorCity: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label><Globe size={16} /> Website</label>
                  <input type="url" placeholder="https://yourbusiness.com" value={form.vendorWebsite} onChange={(e) => setForm({ ...form, vendorWebsite: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Social Media Links</label>
                  <input type="text" placeholder="Facebook, Instagram, TikTok URLs (comma separated)" value={form.vendorSocialMedia} onChange={(e) => setForm({ ...form, vendorSocialMedia: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label><Calendar size={16} /> Years in Business</label>
                    <input type="number" placeholder="e.g. 5" min="0" max="100" value={form.yearsInBusiness} onChange={(e) => setForm({ ...form, yearsInBusiness: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label><Hash size={16} /> VAT/PAN Number</label>
                    <input type="text" placeholder="Optional" value={form.vatPanNumber} onChange={(e) => setForm({ ...form, vatPanNumber: e.target.value })} />
                  </div>
                </div>
              </>
            )}

            {form.role === 'helper' && (
              <>
                <div className="form-group">
                  <label><Key size={16} /> Couple's Invite Code</label>
                  <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 6px 0' }}>
                    Enter the code provided by the couple. The system will automatically detect your role (Helper or Delegate).
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Enter invite code"
                      value={form.inviteCode}
                      onChange={(e) => { setForm({ ...form, inviteCode: e.target.value.toUpperCase() }); setCodeValidation(null); }}
                      maxLength={8}
                      required
                      style={{ flex: 1, textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 }}
                    />
                    <button type="button" className="btn btn-outline" onClick={handleValidateCode} disabled={validatingCode || !form.inviteCode.trim()}>
                      {validatingCode ? '...' : 'Verify'}
                    </button>
                  </div>
                  {codeValidation?.valid && (
                    <p style={{ color: '#16a34a', fontSize: '13px', marginTop: '6px' }}>
                      ✅ Valid! You'll join <strong>{codeValidation.wedding.names}</strong>'s wedding as a <strong style={{ textTransform: 'capitalize' }}>{codeValidation.role}</strong>
                    </p>
                  )}
                  {codeValidation?.valid === false && (
                    <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '6px' }}>
                      ❌ Invalid code. Please check with the couple.
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="form-row">
              <div className="form-group">
                <label><Lock size={16} /> {t('login.password')}</label>
                <input type="password" placeholder={t('register.createPassword')} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
              </div>
              <div className="form-group">
                <label><Lock size={16} /> {t('register.confirmPassword')}</label>
                <input type="password" placeholder={t('register.confirmPassword')} value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required />
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? t('register.creatingAccount') : t('register.createFreeAccount')}
            </button>
          </form>

          {/* Google creates couple accounts only, and is browser-only (see
              Login.js), so vendors, helpers and the app don't get it. */}
          {form.role === 'couple' && !isNative() && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>{t('login.orContinueWith')}</span>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
              </div>
              <button
                type="button"
                className="btn btn-block"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', fontWeight: 500, padding: '10px 16px', borderRadius: '8px', cursor: 'pointer' }}
                disabled={loading}
                onClick={() => { setError(''); openGoogleSignIn(); }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                Google
              </button>
            </>
          )}

          <div className="auth-footer">
            <p>{t('register.alreadyHaveAccount')} <Link to="/login">{t('register.signIn')}</Link></p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Register;
